import { useState } from "react";
import { motion } from "framer-motion";
import { PenLine, Video, MessageCircle, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const inputCls =
  "w-full rounded-xl border border-[#E6DED1] bg-white px-4 py-3 text-sm text-[#0B2E4A] focus:outline-none focus:ring-2 focus:ring-[#D9A854]/40 focus:border-[#D9A854] transition";

export default function Contact({ settings }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", message: "" });
  const [sending, setSending] = useState(false);

  const phone = settings?.phone || "+39 351 470 7429";
  const meeting = settings?.meeting_link || "https://calendly.com/wydpersonaltravelling/30min";
  const channelsT = t("contact.channels");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, type: "advisory", destination: "Asesoría personalizada" });
      toast.success(t("contact.success"));
      if (window.wydTrack) window.wydTrack("Lead", { content_name: "formulario contato" });
      setForm({ name: "", email: "", whatsapp: "", message: "" });
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const icons = [PenLine, Video, MessageCircle];
  const waLink = `https://wa.me/${phone.replace(/\D/g, "")}`;
  const hrefs = ["#contacto-form", meeting, waLink];
  const values = [
    Array.isArray(channelsT) ? channelsT[0]?.value : "",
    Array.isArray(channelsT) ? channelsT[1]?.value : "",
    Array.isArray(channelsT) ? channelsT[2]?.value : "",
  ];

  return (
    <section id="contacto" data-testid="contact-section" className="py-24 sm:py-32 bg-[#0B2E4A] text-[#FAF6EE] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D9A854] mb-3">{t("contact.eyebrow")}</p>
          <h2 className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance">
            {t("contact.title")}
          </h2>
          <p className="mt-4 text-[#F1E7D6]/70 text-base sm:text-lg leading-relaxed">{t("contact.text")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="space-y-4">
            {Array.isArray(channelsT) && channelsT.map((c, i) => {
              const Icon = icons[i];
              const testids = ["contact-form-card", "contact-meeting-card", "contact-phone-card"];
              return (
                <motion.a
                  key={c.title}
                  href={hrefs[i]}
                  target={i === 0 ? undefined : "_blank"}
                  rel="noopener noreferrer"
                  data-testid={testids[i]}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 hover:bg-white/10 hover:border-[#D9A854]/40 transition-all duration-300"
                >
                  <span className="w-12 h-12 shrink-0 rounded-full bg-[#D9A854]/15 border border-[#D9A854]/30 flex items-center justify-center">
                    <Icon size={20} className="text-[#D9A854]" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[#F1E7D6]/50">{c.title}</span>
                    <span className="block font-display font-semibold text-lg text-[#FAF6EE] truncate mt-0.5">{values[i]}</span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold text-[#D9A854] flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                    {c.cta} <ArrowRight size={15} />
                  </span>
                </motion.a>
              );
            })}
          </div>

          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            id="contacto-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl bg-[#FAF6EE] p-7 sm:p-9 text-[#0B2E4A]"
          >
            <h3 className="font-display font-medium text-2xl">{t("contact.formTitle")}</h3>
            <p className="text-sm text-[#56636D] mt-1 mb-6">{t("contact.formSub")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                data-testid="contact-name-input"
                required
                placeholder={t("contact.namePh")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                placeholder={t("contact.emailPh")}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-whatsapp-input"
                placeholder={t("contact.waPh")}
                value={form.whatsapp}
                onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                className={inputCls}
              />
              <input
                data-testid="contact-message-input"
                placeholder={t("contact.msgPh")}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={inputCls}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="mt-6 w-full bg-[#D9A854] hover:bg-[#C99944] disabled:opacity-60 text-[#0B2E4A] font-semibold px-6 py-4 rounded-full transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {sending ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} />}
              {sending ? t("contact.sending") : t("contact.submit")}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
